import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAbonnement } from '../context/AbonnementContext';
import NavbarAuth from '../components/NavbarAuth';
import StripePaymentModal from '../components/StripePaymentModal';
import api from '../services/api';
import blog1 from '../blog1.png';

// ═══ Hook responsive ═══
const useResponsiveLocal = () => {
    const getState = () => {
        const w = window.innerWidth;
        return { isSmall: w <= 480, isMobile: w <= 768, isTablet: w > 768 && w <= 968 };
    };
    const [state, setState] = useState(getState);
    useEffect(() => {
        const handleResize = () => setState(getState());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return state;
};

const GOLD = '#c0306a';
const Icon = ({ path, size = 18, color = GOLD }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{path}</svg>
);

const icons = {
    check: <polyline points="20 6 9 17 4 12" />,
    crown: <path d="M2 18h20l-2-9-5 4-3-6-3 6-5-4-2 9z" />,
    heart: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
    card: <><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
};

// Avantages affichés selon la formule
const AVANTAGES = [
    'Accès complet à l\'annuaire des membres',
    'Consultation des fiches détaillées',
    'Envoi de demandes illimité',
    'Messagerie avec vos relations acceptées',
    'Intérêts et favoris',
];

const Abonnement = () => {
    const { user } = useAuth();
    const { refresh: refreshAbonnement } = useAbonnement();
    const navigate = useNavigate();
    const { isSmall, isMobile, isTablet } = useResponsiveLocal();
    const compact = isMobile || isTablet;

    const [plans, setPlans] = useState([]);
    const [monAbonnement, setMonAbonnement] = useState(null);
    const [abonnementRequis, setAbonnementRequis] = useState(true);
    const [modePaiement, setModePaiement] = useState('simulation');
    const [loading, setLoading] = useState(true);
    const [paiementEnCours, setPaiementEnCours] = useState(null); // code du plan en cours
    const [error, setError] = useState('');
    const [myPhoto, setMyPhoto] = useState(null);
    // Données du paiement Stripe en cours (ouvre la modale quand renseigné)
    const [stripeData, setStripeData] = useState(null); // { clePublique, clientSecret, orderId, code, plan }

    useEffect(() => {
        fetchPlans();
        fetchMonAbonnement();
        if (user?.id) fetchMyProfile();
    }, [user]);

    const fetchPlans = async () => {
        try {
            const response = await api.get('/abonnements/plans');
            setPlans(response.data.plans);
            setAbonnementRequis(response.data.abonnement_requis);
            setModePaiement(response.data.mode_paiement);
        } catch (err) {
            console.error(err);
            setError('Impossible de charger les formules');
        } finally {
            setLoading(false);
        }
    };

    const fetchMonAbonnement = async () => {
        try {
            const response = await api.get('/abonnements/mon-abonnement');
            setMonAbonnement(response.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchMyProfile = async () => {
        if (!user?.id) return;
        try {
            const response = await api.get(`/profiles/${user.id}`);
            if (response.data) setMyPhoto(response.data.photo_url);
        } catch (err) {
            console.error(err);
        }
    };

    // ═══ Souscription ═══
    const handleSouscrire = async (code) => {
        setPaiementEnCours(code);
        setError('');

        try {
            // 1. Créer la commande côté backend
            const commande = await api.post('/abonnements/commande', { code });
            const { orderId, clientSecret, clePublique, simulation, plan } = commande.data;

            if (simulation) {
                // ═══ MODE SIMULATION ═══
                // Le paiement est validé sans transaction réelle.
                const ok = window.confirm(
                    `MODE SIMULATION\n\nAucun paiement réel ne sera effectué.\n\nConfirmer l'activation de l'abonnement ?`
                );
                if (!ok) {
                    setPaiementEnCours(null);
                    return;
                }

                await api.post('/abonnements/valider', { orderId, code });
                await refreshAbonnement();
                navigate('/abonnement/confirmation');
                return;
            }

            // ═══ MODE STRIPE ═══
            // Ouvre la modale avec le Payment Element.
            if (clientSecret && clePublique) {
                setStripeData({ clePublique, clientSecret, orderId, code, plan });
            } else {
                setError('Le paiement n\'a pas pu être initialisé. Réessayez.');
                setPaiementEnCours(null);
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Erreur lors de la souscription');
            setPaiementEnCours(null);
        }
    };

    // Stripe a confirmé le paiement → on le fait vérifier au backend avant d'activer.
    const handleStripeSuccess = async () => {
        if (!stripeData) return;
        try {
            await api.post('/abonnements/valider', {
                orderId: stripeData.orderId,
                code: stripeData.code,
            });
            await refreshAbonnement();
            setStripeData(null);
            navigate('/abonnement/confirmation');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Le paiement a réussi mais l\'activation a échoué. Contactez le support.');
            setStripeData(null);
            setPaiementEnCours(null);
        }
    };

    const handleStripeClose = () => {
        setStripeData(null);
        setPaiementEnCours(null);
    };

    const formatPrix = (prix, devise) => {
        const n = parseFloat(prix);
        if (n === 0) return 'Gratuit';
        return `${n.toFixed(2).replace('.', ',')} ${devise === 'EUR' ? '€' : devise}`;
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'long', year: 'numeric'
    });

    // Dernier jour d'accès = veille de la date de fin (qui tombe à minuit).
    // Un abonnement expirant le 17 à 00h00 donne accès jusqu'au 16 inclus.
    const formatDateVeille = (d) => {
        const date = new Date(d);
        date.setDate(date.getDate() - 1);
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    // Mise en avant de la formule la plus longue
    const meilleurPlan = plans.reduce(
        (best, p) => (p.duree_mois > (best?.duree_mois || 0) ? p : best),
        null
    );

    const heroStyle = {
        ...styles.hero,
        padding: isSmall ? '28px 16px' : isMobile ? '32px 20px' : '48px 64px',
    };

    const layoutStyle = {
        maxWidth: '1320px',
        margin: '0 auto',
        padding: isSmall ? '12px' : isMobile ? '16px' : '32px',
        boxSizing: 'border-box',
    };

    return (
        <div style={styles.page}>
            <NavbarAuth myPhoto={myPhoto} />

            {/* ═══ HERO ═══ */}
            <div style={heroStyle}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${blog1})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(61,10,38,0.75), rgba(42,5,24,0.55))' }} />
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <p style={styles.breadcrumb}>Accueil › Abonnement</p>
                    <h1 style={{ ...styles.heroTitle, fontSize: isSmall ? '26px' : isMobile ? '32px' : '40px' }}>
                        Choisissez votre <span style={{ color: '#E88BB4' }}>formule</span>
                    </h1>
                    <p style={styles.heroText}>
                        Accédez à l'ensemble des fonctionnalités de Zawaj &amp; Tawadud<br />
                        et trouvez la personne qui vous correspond, incha'Allah.
                    </p>
                </div>
            </div>

            <div style={layoutStyle}>
                <main style={{ ...styles.mainCard, padding: isSmall ? '16px' : isMobile ? '20px' : isTablet ? '28px' : '40px' }}>

                    {/* Bandeau mode simulation */}
                    {modePaiement === 'simulation' && (
                        <div style={styles.simuBanner}>
                            <Icon path={icons.shield} size={16} color="#8b1a4a" />
                            <span>
                                <strong>Mode test</strong> — aucun paiement réel n'est effectué.
                                Le système de paiement sera activé prochainement.
                            </span>
                        </div>
                    )}

                    {error && <div style={styles.error}>{error}</div>}

                    {/* ═══ Abonnement en cours ═══ */}
                    {monAbonnement?.abonnement && (
                        <div style={styles.currentBox}>
                            <div style={styles.currentHead}>
                                <Icon path={icons.crown} size={20} color="#c0306a" />
                                <h3 style={styles.currentTitle}>Votre abonnement est actif</h3>
                            </div>
                            <div style={styles.currentGrid}>
                                <div style={styles.currentItem}>
                                    <span style={styles.currentLabel}>Formule</span>
                                    <span style={styles.currentVal}>
                                        {plans.find(p => p.code === monAbonnement.abonnement.code)?.libelle || monAbonnement.abonnement.code}
                                    </span>
                                </div>
                                <div style={styles.currentItem}>
                                    <span style={styles.currentLabel}>Depuis le</span>
                                    <span style={styles.currentVal}>{formatDate(monAbonnement.abonnement.date_debut)}</span>
                                </div>
                                <div style={styles.currentItem}>
                                    <span style={styles.currentLabel}>Expire le</span>
                                    <span style={{ ...styles.currentVal, color: '#c0306a' }}>
                                        {formatDateVeille(monAbonnement.abonnement.date_fin)}
                                    </span>
                                </div>
                            </div>
                            <p style={styles.currentNote}>
                                Une nouvelle souscription sera possible à l'échéance de votre abonnement,
                                le {formatDate(monAbonnement.abonnement.date_fin)}.
                            </p>
                        </div>
                    )}

                    {/* ═══ Accès gratuit ═══ */}
                    {!abonnementRequis && !monAbonnement?.abonnement && (
                        <div style={styles.gratuitBox}>
                            <Icon path={icons.heart} size={22} color="#c0306a" />
                            <div>
                                <h3 style={styles.gratuitTitle}>Votre accès est gratuit</h3>
                                <p style={styles.gratuitText}>
                                    En tant que membre féminin, vous bénéficiez d'un accès à l'annuaire,
                                    à l'envoi de demandes et à la messagerie sans aucun frais.
                                    Vous pouvez souscrire à une formule pour débloquer les fiches détaillées.
                                </p>
                            </div>
                        </div>
                    )}

                    <h2 style={{ ...styles.title, fontSize: isSmall ? '22px' : isMobile ? '25px' : '30px' }}>
                        <Icon path={icons.crown} size={isSmall ? 20 : 26} color="#4a0028" />
                        Nos formules
                    </h2>

                    {loading ? (
                        <div style={styles.loading}>Chargement des formules...</div>
                    ) : (
                        <div style={{
                            ...styles.plansGrid,
                            gridTemplateColumns: isSmall ? '1fr' : isMobile ? '1fr' : isTablet ? '1fr 1fr' : `repeat(${Math.min(plans.length, 4)}, 1fr)`,
                        }}>
                            {plans.map(plan => {
                                const gratuit = parseFloat(plan.prix) === 0;
                                const populaire = meilleurPlan && plan.code === meilleurPlan.code && !gratuit;
                                // L'abonnement n'est pas requis pour ce membre (réglage admin
                                // par genre) : les formules restent visibles mais désactivées.
                                const nonRequis = monAbonnement && monAbonnement.requis === false && !gratuit;
                                // Un abonnement en cours bloque toute nouvelle souscription
                                // jusqu'à son échéance.
                                const bloqueParAbonnement = (!!monAbonnement?.abonnement || nonRequis) && !gratuit;

                                return (
                                    <div key={plan.code} style={populaire && !bloqueParAbonnement ? styles.planCardPopulaire : styles.planCard}>
                                        {populaire && !bloqueParAbonnement && <div style={styles.populaireBadge}>Recommandé</div>}
                                        {monAbonnement?.abonnement && plan.code === monAbonnement.abonnement.code && (
                                            <div style={styles.actuelBadge}>Formule en cours</div>
                                        )}

                                        <div style={styles.planHead}>
                                            <h3 style={styles.planName}>{plan.libelle}</h3>
                                            <div style={styles.planPrix}>
                                                {formatPrix(plan.prix, plan.devise)}
                                            </div>
                                            {plan.duree_mois > 0 && (
                                                <p style={styles.planDuree}>
                                                    <Icon path={icons.calendar} size={12} color="#9a7d88" />
                                                    {plan.duree_mois} mois d'accès
                                                </p>
                                            )}
                                            {/* ═══ Offre de lancement (À RETIRER en octobre) ═══ */}
                                            {plan.duree_mois === 1 && (
                                                <p style={styles.offreLancement}>
                                                    3,90 € jusqu'au 30 septembre, puis 6,90 € à partir d'octobre
                                                </p>
                                            )}
                                        </div>

                                        <ul style={styles.avantagesList}>
                                            {AVANTAGES.map((a, i) => (
                                                <li key={i} style={styles.avantageItem}>
                                                    <Icon path={icons.check} size={14} color="#c0306a" />
                                                    <span>{a}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {gratuit ? (
                                            <button disabled style={styles.planBtnGratuit}>
                                                <Icon path={icons.check} size={14} color="#9a7d88" /> Formule actuelle
                                            </button>
                                        ) : bloqueParAbonnement ? (
                                            <button disabled style={styles.planBtnBloque}
                                                title={nonRequis
                                                    ? 'L\'abonnement n\'est pas nécessaire pour votre profil'
                                                    : `Disponible après le ${formatDate(monAbonnement.abonnement.date_fin)}`}>
                                                <Icon path={icons.lock} size={14} color="#9a7d88" />
                                                {nonRequis ? 'Non nécessaire' : 'Indisponible'}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleSouscrire(plan.code)}
                                                disabled={paiementEnCours !== null}
                                                style={populaire ? styles.planBtnPopulaire : styles.planBtn}
                                            >
                                                <Icon path={icons.card} size={14} color="#f3e0c7" />
                                                {paiementEnCours === plan.code ? 'Traitement...' : 'Souscrire'}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* ═══ Moyens de paiement ═══ */}
                    <div style={styles.paiementInfo}>
                        <Icon path={icons.lock} size={15} color="#9a7d88" />
                        <span>
                            Paiement sécurisé par carte bancaire via Stripe.
                            Vos données bancaires ne transitent jamais par nos serveurs.
                        </span>
                    </div>
                </main>
            </div>

            {/* ═══ MODALE DE PAIEMENT STRIPE ═══ */}
            {stripeData && (
                <StripePaymentModal
                    clePublique={stripeData.clePublique}
                    clientSecret={stripeData.clientSecret}
                    plan={stripeData.plan}
                    onSuccess={handleStripeSuccess}
                    onClose={handleStripeClose}
                />
            )}
        </div>
    );
};

const styles = {
    page: { minHeight: '100vh', width: '100%', boxSizing: 'border-box', overflowX: 'hidden', background: 'linear-gradient(160deg, #fdfdfe 0%, #f5f6f8 50%, #eef0f3 100%)', backgroundAttachment: 'fixed', fontFamily: 'Arial, sans-serif' },
    mainCard: { background: '#ffffff', borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', minWidth: 0 },

    hero: { position: 'relative', overflow: 'hidden', color: 'white' },
    breadcrumb: { color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '12px' },
    heroTitle: { fontWeight: '700', margin: '0 0 16px', fontFamily: 'Cormorant Garamond, serif' },
    heroText: { color: 'rgba(255,255,255,0.78)', fontSize: '14px', lineHeight: '1.8', margin: 0 },

    title: { color: '#4a0028', fontWeight: '800', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: '12px' },

    simuBanner: { display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(60,60,80,0.05)', border: '1px solid #f5d0e0', borderRadius: '12px', padding: '14px 18px', marginBottom: '24px', color: '#c0306a', fontSize: '13.5px', lineHeight: '1.5' },

    currentBox: { background: 'rgba(60,60,80,0.04)', border: '1px solid #f5d0e0', borderRadius: '16px', padding: '22px 24px', marginBottom: '28px' },
    currentHead: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' },
    currentTitle: { color: '#c0306a', fontSize: '18px', fontWeight: '800', margin: 0 },
    currentGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' },
    currentItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
    currentLabel: { color: '#9a7d88', fontSize: '12px', fontWeight: '600' },
    currentVal: { color: '#4a0028', fontSize: '15px', fontWeight: '800' },
    currentNote: { color: '#9a7d88', fontSize: '12.5px', lineHeight: '1.6', margin: '16px 0 0', fontStyle: 'italic' },

    gratuitBox: { display: 'flex', alignItems: 'flex-start', gap: '14px', background: 'rgba(60,60,80,0.04)', border: '1px solid #f5d0e0', borderRadius: '16px', padding: '22px 24px', marginBottom: '28px' },
    gratuitTitle: { color: '#4a0028', fontSize: '17px', fontWeight: '800', margin: '0 0 8px' },
    gratuitText: { color: '#c0306a', fontSize: '13.5px', lineHeight: '1.6', margin: 0 },

    plansGrid: { display: 'grid', gap: '18px', alignItems: 'stretch' },
    planCard: { position: 'relative', background: '#fff', borderRadius: '16px', padding: '26px 22px', border: '1px solid #f5d0e0', boxShadow: '0 4px 16px rgba(74,0,40,0.08)', display: 'flex', flexDirection: 'column' },
    planCardPopulaire: { position: 'relative', background: '#fff', borderRadius: '16px', padding: '26px 22px', border: '2px solid #c0306a', boxShadow: '0 8px 28px rgba(192,48,106,0.12)', display: 'flex', flexDirection: 'column' },
    populaireBadge: { position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', background: '#c0306a', color: '#f3e0c7', padding: '4px 16px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', whiteSpace: 'nowrap' },
    actuelBadge: { position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #c0306a, #8b1a4a)', color: '#fff', padding: '4px 16px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', whiteSpace: 'nowrap' },

    planHead: { textAlign: 'center', paddingBottom: '18px', borderBottom: '1px solid #f0e4e9', marginBottom: '18px' },
    planName: { color: '#8b1a4a', fontSize: '19px', fontWeight: '700', margin: '0 0 10px', fontFamily: 'Cormorant Garamond, serif' },
    planPrix: { color: '#4a0028', fontSize: '30px', fontWeight: '800', marginBottom: '6px' },
    planDuree: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: '#9a7d88', fontSize: '12.5px', margin: 0 },
    offreLancement: { textAlign: 'center', color: '#c0306a', fontSize: '12px', fontWeight: '600', marginTop: '8px', marginBottom: 0, lineHeight: 1.4 },

    avantagesList: { listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 },
    avantageItem: { display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#5a4a52', fontSize: '12.5px', lineHeight: '1.5' },

    planBtn: { width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13.5px', boxShadow: '0 3px 12px rgba(74,0,40,0.25)' },
    planBtnPopulaire: { width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '800', fontSize: '13.5px', boxShadow: '0 3px 12px rgba(192,48,106,0.15)' },
    planBtnGratuit: { width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: 'rgba(60,60,80,0.05)', color: '#9a7d88', border: 'none', borderRadius: '10px', cursor: 'not-allowed', fontWeight: '700', fontSize: '13.5px' },
    planBtnBloque: { width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: 'rgba(154,125,136,0.1)', color: '#9a7d88', border: '1.5px dashed #d4c0c8', borderRadius: '10px', cursor: 'not-allowed', fontWeight: '700', fontSize: '13.5px' },

    paiementInfo: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #f0d9e3', color: '#9a7d88', fontSize: '12.5px', textAlign: 'center' },

    loading: { textAlign: 'center', color: '#4a0028', fontSize: '16px', fontWeight: '600', padding: '40px 0' },
    error: { backgroundColor: '#f2f4f7', color: '#c00', padding: '12px', borderRadius: '10px', marginBottom: '16px', textAlign: 'center' },
};

export default Abonnement;